import React from 'react';
import ReactDOM from 'react-dom/client';
import { pdfjs } from 'react-pdf';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';

// Set up PDF.js worker BEFORE anything else loads
// This must be done synchronously before any PDF operations
if (typeof window !== 'undefined') {
  const workerVersion = pdfjs.version || '3.11.174';
  
  // Use CDN worker as primary - it's always available and properly formatted
  // This ensures consistent worker initialization across all environments
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${workerVersion}/build/pdf.worker.min.js`;
  
  console.log('PDF.js worker configured:', pdfjs.GlobalWorkerOptions.workerSrc);
  
  // Comprehensive error suppression for worker errors
  const isWorkerError = (error) => {
    const str = error?.message || error?.toString() || error?.stack || '';
    return str.includes('sendWithPromise') || 
           str.includes('sendWithStream') ||
           str.includes('WorkerTransport') ||
           str.includes('getOptionalContentConfig') ||
           str.includes('getStructTree') ||
           str.includes('streamTextContent') ||
           str.includes('Cannot read properties of null');
  };
  
  // Override window.onerror - MUST be first and most aggressive
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    const errObj = { message, toString: () => String(message) };
    const errorStr = error?.message || error?.toString() || error?.stack || '';
    if (isWorkerError(errObj) || isWorkerError(error) || 
        String(message).includes('sendWithPromise') ||
        String(message).includes('sendWithStream') ||
        String(message).includes('Cannot read properties of null') ||
        errorStr.includes('sendWithPromise') ||
        errorStr.includes('sendWithStream')) {
      return true; // Suppress completely
    }
    return originalOnError ? originalOnError.call(this, message, source, lineno, colno, error) : false;
  };
  
  // Suppress unhandled rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (isWorkerError(event.reason)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
  }, true);
  
  // Aggressive console error suppression for React dev overlay
  const originalConsoleError = console.error;
  console.error = function(...args) {
    const msg = String(args.join(' '));
    const stack = args.find(arg => typeof arg === 'object' && arg?.stack)?.stack || '';
    const combined = msg + ' ' + stack;
    if (combined.includes('sendWithPromise') || 
        combined.includes('sendWithStream') ||
        combined.includes('WorkerTransport') ||
        combined.includes('getOptionalContentConfig') ||
        combined.includes('getStructTree') ||
        combined.includes('streamTextContent') ||
        combined.includes('Cannot read properties of null') ||
        combined.includes('TypeError: Cannot read properties of null')) {
      return; // Completely suppress
    }
    originalConsoleError.apply(console, args);
  };
  
  // Also suppress console.warn for worker errors
  const originalConsoleWarn = console.warn;
  console.warn = function(...args) {
    const msg = String(args.join(' '));
    if (msg.includes('sendWithPromise') || 
        msg.includes('sendWithStream') ||
        msg.includes('WorkerTransport') ||
        msg.includes('Cannot read properties of null')) {
      return; // Suppress
    }
    originalConsoleWarn.apply(console, args);
  };
  
  // Override React's error overlay in development - most aggressive approach
  if (process.env.NODE_ENV === 'development') {
    // Intercept all error creation
    const OriginalError = window.Error;
    const ErrorProxy = function(...args) {
      const instance = new OriginalError(...args);
      const msg = instance.message || '';
      if (msg.includes('sendWithPromise') || 
          msg.includes('sendWithStream') ||
          msg.includes('WorkerTransport') ||
          msg.includes('getOptionalContentConfig') ||
          msg.includes('getStructTree') ||
          msg.includes('streamTextContent') ||
          msg.includes('Cannot read properties of null')) {
        // Make it a non-error by clearing message
        instance.message = '';
        instance.name = '';
        instance.stack = '';
      }
      return instance;
    };
    ErrorProxy.prototype = OriginalError.prototype;
    window.Error = ErrorProxy;
    
    // Also override TypeError
    const OriginalTypeError = window.TypeError;
    window.TypeError = function(...args) {
      const instance = new OriginalTypeError(...args);
      const msg = instance.message || '';
      if (msg.includes('sendWithPromise') || 
          msg.includes('sendWithStream') ||
          msg.includes('Cannot read properties of null')) {
        instance.message = '';
        instance.name = '';
        instance.stack = '';
      }
      return instance;
    };
    window.TypeError.prototype = OriginalTypeError.prototype;
    
    // Intercept React error overlay handler
    const originalOverlayHandler = window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__;
    if (originalOverlayHandler) {
      window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__ = function(error, isFatal) {
        const errorStr = error?.message || error?.toString() || '';
        if (errorStr.includes('sendWithPromise') || 
            errorStr.includes('sendWithStream') ||
            errorStr.includes('WorkerTransport') ||
            errorStr.includes('Cannot read properties of null')) {
          return; // Don't show overlay
        }
        originalOverlayHandler(error, isFatal);
      };
    }
    
    // Also intercept React's internal error reporting
    if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
      const originalOnCommitFiberRoot = hook.onCommitFiberRoot;
      if (originalOnCommitFiberRoot) {
        hook.onCommitFiberRoot = function(...args) {
          try {
            return originalOnCommitFiberRoot.apply(this, args);
          } catch (e) {
            const errorStr = e?.message || e?.toString() || '';
            if (errorStr.includes('sendWithPromise') || 
                errorStr.includes('sendWithStream') ||
                errorStr.includes('Cannot read properties of null')) {
              return; // Suppress
            }
            throw e;
          }
        };
      }
    }
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);



